import type { LucideIcon, LucideProps } from "lucide-react";
import {
  Palette,
  FileCheck,
  PenTool,
  Wrench,
  ArchiveRestore,
  Shirt,
  RefreshCw,
  ShieldCheck,
  PackageCheck,
  CheckCircle2,
  ShoppingCart,
  Tag,
} from "lucide-react";

import sewoutIcon from "@assets/new_sample_1771873825200.png";

export const SampleStampIcon = ({ style, className, ...props }: LucideProps) => {
  // Sizing priority: explicit props/style -> className -> 24px fallback (only when caller gave no sizing at all).
  const sizeFromStyle = style?.width ?? style?.height;
  const explicit = (props.width as number | string | undefined) || (props.height as number | string | undefined) || sizeFromStyle;
  const dimsFromExplicit = explicit ? { width: explicit, height: explicit } : {};
  const needsDefaultSize = !explicit && !className;
  // Color priority: explicit style.color → currentColor (so a parent `color` cascades, matching lucide-react icons).
  const color = (style?.color as string) || "currentColor";
  return (
    <div
      className={className}
      style={{
        ...style,
        ...(needsDefaultSize ? { width: 24, height: 24 } : {}),
        ...dimsFromExplicit,
        backgroundColor: color,
        WebkitMaskImage: `url(${sewoutIcon})`,
        WebkitMaskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskImage: `url(${sewoutIcon})`,
        maskSize: "contain",
        maskRepeat: "no-repeat",
        maskPosition: "center",
      }}
    />
  );
};

export const InkDripIcon = (props: LucideProps) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 2 C12 2 11.5 4 11.5 4 C11 6 10.5 8 10.5 8 C10 10 9.5 11.5 9 13 C8.2 15.2 8 16.5 8 17.5 C8 20 9.8 22 12 22 C14.2 22 16 20 16 17.5 C16 16.5 15.8 15.2 15 13 C14.5 11.5 14 10 13.5 8 C13 6 12.5 4 12.5 4 Z" />
  </svg>
);

export const PrintingPressIcon = (props: LucideProps) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="2" y="4" width="20" height="10" rx="1.5" />
    <rect x="4" y="6" width="5" height="6" rx="0.5" />
    <rect x="10" y="6" width="5" height="6" rx="0.5" />
    <circle cx="18" cy="9" r="2" />
    <line x1="4" y1="14" x2="4" y2="18" />
    <line x1="20" y1="14" x2="20" y2="18" />
    <rect x="2" y="18" width="20" height="3" rx="1" />
  </svg>
);

export const MeshScreenIcon = (props: LucideProps) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="5" y="2" width="14" height="20" rx="2" />
    <line x1="5" y1="6" x2="19" y2="6" />
    <line x1="5" y1="10" x2="19" y2="10" />
    <line x1="5" y1="14" x2="19" y2="14" />
    <line x1="5" y1="18" x2="19" y2="18" />
    <line x1="9" y1="2" x2="9" y2="22" />
    <line x1="13" y1="2" x2="13" y2="22" />
    <line x1="17" y1="2" x2="17" y2="22" />
  </svg>
);

export const STEP_ICONS: Record<string, LucideIcon> = {
  art_sent_digitizing: Palette,
  prt_files_sent: FileCheck,
  dst_files_created: PenTool,
  screens_burned: MeshScreenIcon as unknown as LucideIcon,
  ink_mixed: InkDripIcon as unknown as LucideIcon,
  press_setup: Wrench,
  blank_product_received: ArchiveRestore,
  hp_blank_product_received: ArchiveRestore,
  product_laid_out: Shirt,
  emb_product_laid_out: Shirt,
  hp_product_laid_out: Shirt,
  production_run: RefreshCw,
  emb_production_run: RefreshCw,
  hp_production_run: RefreshCw,
  pb_production_run: RefreshCw,
  quality_check: ShieldCheck,
  emb_quality_check: ShieldCheck,
  hp_quality_check: ShieldCheck,
  pb_quality_check: ShieldCheck,
  packing: PackageCheck,
  emb_packing: PackageCheck,
  hp_packing: PackageCheck,
  pb_packing: PackageCheck,
  sample_sewout: SampleStampIcon as unknown as LucideIcon,
  sewout_approved: CheckCircle2,
  hp_transfers_ordered: ShoppingCart,
  hp_transfers_received: ArchiveRestore,
  pb_finished_goods_laid_out: Shirt,
  pb_sku_label_art_ready: Tag,
};

export const STEP_LABELS: Record<string, string> = {
  art_sent_digitizing: "Art Sent",
  prt_files_sent: "SEP Files",
  dst_files_created: "DST Files",
  screens_burned: "Screens",
  ink_mixed: "Ink Mixed",
  press_setup: "Press Setup",
  blank_product_received: "Product In",
  hp_blank_product_received: "Product In",
  product_laid_out: "Laid Out",
  emb_product_laid_out: "Laid Out",
  hp_product_laid_out: "Laid Out",
  sample_sewout: "Sewout",
  sewout_approved: "Approved",
  hp_transfers_ordered: "Transfers Ordered",
  hp_transfers_received: "Transfers In",
  pb_finished_goods_laid_out: "Goods Laid Out",
  pb_sku_label_art_ready: "Label Art",
  production_run: "Production",
  emb_production_run: "Production",
  hp_production_run: "Production",
  pb_production_run: "Production",
  quality_check: "QC",
  emb_quality_check: "QC",
  hp_quality_check: "QC",
  pb_quality_check: "QC",
  packing: "Packed",
  emb_packing: "Packed",
  hp_packing: "Packed",
  pb_packing: "Packed",
};

export const PRODUCTION_RUN_KEYS: Record<string, string> = {
  screenprint: "production_run",
  embroidery: "emb_production_run",
  heat_press: "hp_production_run",
  poly_bagging: "pb_production_run",
};

export const PIPELINE_STEPS: Record<string, string[]> = {
  screenprint: [
    "prt_files_sent", "ink_mixed", "blank_product_received",
    "screens_burned", "product_laid_out",
    "press_setup", "production_run", "quality_check", "packing",
  ],
  embroidery: [
    "art_sent_digitizing", "blank_product_received",
    "dst_files_created", "emb_product_laid_out",
    "sample_sewout", "sewout_approved",
    "emb_production_run", "emb_quality_check", "emb_packing",
  ],
  heat_press: [
    "hp_transfers_ordered", "hp_blank_product_received",
    "hp_transfers_received", "hp_product_laid_out",
    "hp_production_run", "hp_quality_check", "hp_packing",
  ],
  poly_bagging: [
    "pb_finished_goods_laid_out", "pb_sku_label_art_ready",
    "pb_production_run", "pb_quality_check", "pb_packing",
  ],
};
